import React, { createContext, useContext, useEffect, useState } from 'react';

export type Language = 'ru' | 'ky' | 'en';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const translations: Record<Language, Record<string, string>> = {
  ru: {
    new: 'Новые',
    list: 'Список',
    history: 'История',
    tin: 'ИНН',
    tinPlaceholder: 'Введите 14 цифр',
    selectUser: 'Выберите пользователя',
    documentUpload: 'Документ на подпись',
    uploadFile: 'Загрузить файл',
    submit: 'Подписать / Выставить',
    issuedBy: 'Кем выставлен',
    issuedTo: 'Кому выставлен',
    status: 'Статус',
    pending: 'Ожидает',
    signed: 'Подписан',
    rejected: 'Отклонен',
    success: 'Успешно!',
    documentSigned: 'Документ успешно подписан',
  },
  ky: {
    new: 'Жаңы',
    list: 'Тизме',
    history: 'Тарых',
    tin: 'ИНН',
    tinPlaceholder: '14 санды киргизиңиз',
    selectUser: 'Колдонуучуну тандаңыз',
    documentUpload: 'Кол коюуга документ',
    uploadFile: 'Файл жүктөө',
    submit: 'Кол коюу / Чыгаруу',
    issuedBy: 'Ким тарабынан чыгарылды',
    issuedTo: 'Кимге чыгарылды',
    status: 'Статус',
    pending: 'Күтүүдө',
    signed: 'Кол коюлду',
    rejected: 'Четке кагылды',
    success: 'Ийгиликтүү!',
    documentSigned: 'Документке ийгиликтүү кол коюлду',
  },
  en: {
    new: 'New',
    list: 'List',
    history: 'History',
    tin: 'TIN',
    tinPlaceholder: 'Enter 14 digits',
    selectUser: 'Select user',
    documentUpload: 'Document to sign',
    uploadFile: 'Upload file',
    submit: 'Sign / Issue',
    issuedBy: 'Issued by',
    issuedTo: 'Issued to',
    status: 'Status',
    pending: 'Pending',
    signed: 'Signed',
    rejected: 'Rejected',
    success: 'Success!',
    documentSigned: 'Document signed successfully',
  },
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguage] = useState<Language>('ru');

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const langParam = params.get('lang');
    if (langParam === 'ru' || langParam === 'ky' || langParam === 'en') {
      setLanguage(langParam);
    }
  }, []);

  const t = (key: string): string => {
    return translations[language][key] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
};
